import { useState } from 'react';
import {
  CAlert, CBadge, CButton, CCard, CCardBody, CCardHeader, CCol, CFormInput, CFormLabel, CFormSelect,
  CFormSwitch, CFormTextarea, CModal, CModalBody, CModalFooter, CModalHeader, CModalTitle, CRow,
} from '@coreui/react';
import { api, problemMessage, type Schemas } from '../../api/client';
import { CUSTOM_DEVICE_TYPE, DeviceTypeSelect, deviceChosen } from '../../components/DeviceTypeSelect';
import { useLookups } from '../../hooks/useLookups';

type Contract = Schemas['ContractResponse'];
type Item = Schemas['ContractItemResponse'];

type Props = { contract: Contract; onChanged: (c: Contract) => void };

/**
 * Položky zakázky a jejich konfigurace. Měnit jdou, dokud zakázka není hotová nebo zrušená —
 * tedy i ve výrobě, protože sestavitel doplňuje, co zjistil (ADR-015). Jestli jde upravovat,
 * říká `areItemsEditable` z API, frontend to nepočítá.
 */
export function ItemsSection({ contract, onChanged }: Props) {
  const [editing, setEditing] = useState<Item | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const editable = contract.areItemsEditable;

  async function remove(item: Item) {
    if (!window.confirm(`Odebrat položku #${item.lineNumber} (${item.quantity}× ${item.deviceTypeName})?`)) return;

    const { data, error: problem } = await api.DELETE('/api/v1/contracts/{contractId}/items/{itemId}', {
      params: { path: { contractId: contract.id, itemId: item.id }, query: { expectedRowVersion: contract.rowVersion } },
    });

    if (data) onChanged(data); else setError(problemMessage(problem));
  }

  async function changeQuantity(item: Item, quantity: number) {
    const { data, error: problem } = await api.PATCH('/api/v1/contracts/{contractId}/items/{itemId}', {
      params: { path: { contractId: contract.id, itemId: item.id } },
      body: { quantity, expectedRowVersion: contract.rowVersion },
    });

    if (data) onChanged(data); else setError(problemMessage(problem));
  }

  return (
    <CCard className="mb-4">
      <CCardHeader className="d-flex justify-content-between align-items-center">
        <strong>Položky</strong>
        {editable && <CButton size="sm" color="primary" variant="ghost" onClick={() => setAdding(true)}>+ Položka</CButton>}
      </CCardHeader>
      <CCardBody>
        {error && <CAlert color="danger">{error}</CAlert>}
        {contract.items.length === 0 && <div className="text-body-secondary">Zakázka nemá žádnou položku.</div>}

        {contract.items.map((item, index) => (
          <div key={item.id} className={index < contract.items.length - 1 ? 'border-bottom pb-3 mb-3' : ''}>
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <span className="text-body-secondary me-2">#{item.lineNumber}</span>
                {editable ? (
                  <input
                    type="number" min={1} max={9999} defaultValue={item.quantity}
                    className="form-control form-control-sm d-inline-block me-1" style={{ width: 72 }}
                    onBlur={(e) => { const q = Number(e.target.value); if (q >= 1 && q !== item.quantity) void changeQuantity(item, q); }}
                    aria-label="Počet kusů"
                  />
                ) : <strong>{item.quantity}</strong>}
                <span className="fw-semibold">× {item.deviceTypeName}</span>
                {item.portalDeviceTypeId === CUSTOM_DEVICE_TYPE && <CBadge color="secondary" className="ms-2 align-middle">na míru</CBadge>}
                {item.label && <span className="text-body-secondary"> — {item.label}</span>}
              </div>
              {editable && (
                <div className="d-flex gap-2">
                  {/* Plne tlacitko jen kdyz konfigurace chybi - to je prace, ktera ceka. */}
                  <CButton size="sm" color="primary" variant={item.configuration ? 'ghost' : undefined} onClick={() => setEditing(item)}
                    data-tour={item.configuration ? 'edit-item' : 'configure-item'}>
                    {item.configuration ? 'Upravit' : 'Vyplnit konfiguraci'}
                  </CButton>
                  <CButton size="sm" color="secondary" variant="ghost" onClick={() => void remove(item)}>Odebrat</CButton>
                </div>
              )}
            </div>
            {item.note && <div className="small mt-1">{item.note}</div>}
            <ConfigurationSummary item={item} />
          </div>
        ))}
      </CCardBody>

      {editing && (
        <ConfigurationEditor contract={contract} item={editing}
          onClose={() => setEditing(null)} onSaved={(c) => { setEditing(null); onChanged(c); }} />
      )}
      {adding && (
        <AddItem contract={contract} onClose={() => setAdding(false)} onSaved={(c) => { setAdding(false); onChanged(c); }} />
      )}
    </CCard>
  );
}

function ConfigurationSummary({ item }: { item: Item }) {
  const cfg = item.configuration;

  if (!cfg) {
    // Presne tenhle stav blokuje "Schvalit k vyrobe" - musi byt videt na prvni pohled.
    return <CBadge color="danger" className="mt-2">Konfigurace není vyplněná</CBadge>;
  }

  const yesNo = (v: boolean) => (v ? 'ano' : 'ne');

  return (
    <div className="mt-2 small">
      <div className="mb-2">
        <span className="me-3">Krabice: <strong>{cfg.enclosureTypeName ?? '—'}{cfg.enclosureCustomText && ` (${cfg.enclosureCustomText})`}</strong></span>
        <span className="me-3">GPS: <strong>{yesNo(cfg.hasGps)}</strong></span>
        <span className="me-3">LTE: <strong>{yesNo(cfg.hasLte)}</strong></span>
        <span className="me-3">WiFi: <strong>{yesNo(cfg.hasWifi)}</strong></span>
        <span className="me-3">LoRa: <strong>{yesNo(cfg.hasLora)}</strong></span>
        {cfg.firmwareVersion && <span>Firmware: <strong>{cfg.firmwareVersion}</strong></span>}
      </div>
      {cfg.parameters.length > 0 && (
        <table className="table table-sm mb-0" style={{ maxWidth: 560 }}>
          <tbody>
            {cfg.parameters.map((p) => (
              <tr key={p.key}><td className="text-body-secondary" style={{ width: '40%' }}>{p.key}</td><td className="fw-semibold">{p.value || '—'}</td></tr>
            ))}
          </tbody>
        </table>
      )}
      {cfg.notes && <div className="mt-2">{cfg.notes}</div>}
    </div>
  );
}

/**
 * Nahrazuje celou konfiguraci — posílá se cílový stav, ne změna (ADR-015). Místo pevného
 * rastru IO1–IO8 volné parametry klíč → hodnota: každá zakázka má jiná specifika.
 */
function ConfigurationEditor({ contract, item, onClose, onSaved }: {
  contract: Contract; item: Item; onClose: () => void; onSaved: (c: Contract) => void;
}) {
  const lookups = useLookups();
  const cfg = item.configuration;

  const [enclosure, setEnclosure] = useState(cfg?.enclosureTypeCode ?? '');
  const [enclosureText, setEnclosureText] = useState(cfg?.enclosureCustomText ?? '');
  const [gps, setGps] = useState(cfg?.hasGps ?? false);
  const [lte, setLte] = useState(cfg?.hasLte ?? false);
  const [wifi, setWifi] = useState(cfg?.hasWifi ?? false);
  const [lora, setLora] = useState(cfg?.hasLora ?? false);
  const [firmware, setFirmware] = useState(cfg?.firmwareVersion ?? '');
  const [notes, setNotes] = useState(cfg?.notes ?? '');
  const [parameters, setParameters] = useState<{ key: string; value: string }[]>(
    cfg?.parameters.length ? cfg.parameters.map((p) => ({ ...p })) : [{ key: '', value: '' }]);

  const setParameter = (index: number, patch: Partial<{ key: string; value: string }>) =>
    setParameters(parameters.map((p, i) => (i === index ? { ...p, ...patch } : p)));
  const [error, setError] = useState<string | null>(null);

  const needsText = lookups?.enclosureTypes.find((e) => e.code === enclosure)?.requiresCustomText ?? false;

  async function save() {
    const { data, error: problem } = await api.PUT('/api/v1/contracts/{contractId}/items/{itemId}/configuration', {
      params: { path: { contractId: contract.id, itemId: item.id } },
      body: {
        expectedRowVersion: contract.rowVersion,
        configuration: {
          enclosureTypeCode: enclosure || null,
          enclosureCustomText: needsText ? enclosureText : null,
          hasGps: gps,
          hasLte: lte,
          hasWifi: wifi,
          hasLora: lora,
          firmwareVersion: firmware || null,
          notes: notes || null,
          // Radek bez klice server zahodi - prazdny posledni radek nevadi.
          parameters: parameters.map((p) => ({ key: p.key, value: p.value })),
        },
      },
    });

    if (data) onSaved(data); else setError(problemMessage(problem));
  }

  return (
    <CModal visible size="lg" onClose={onClose}>
      <CModalHeader><CModalTitle>#{item.lineNumber} · {item.quantity}× {item.deviceTypeName} — konfigurace</CModalTitle></CModalHeader>
      <CModalBody>
        <CRow className="g-3 mb-3">
          <CCol md={6}>
            <CFormLabel>Krabice</CFormLabel>
            <CFormSelect value={enclosure} onChange={(e) => setEnclosure(e.target.value)}>
              <option value="">—</option>
              {lookups?.enclosureTypes.map((e) => <option key={e.code} value={e.code}>{e.name}</option>)}
            </CFormSelect>
            {needsText && (
              <CFormInput className="mt-2" placeholder="Jaká krabice?" value={enclosureText} onChange={(e) => setEnclosureText(e.target.value)} />
            )}
          </CCol>
          <CCol md={6}>
            <CFormLabel>Firmware</CFormLabel>
            <CFormInput value={firmware} onChange={(e) => setFirmware(e.target.value)} placeholder="např. 2.4.1" />
          </CCol>
        </CRow>

        <div className="d-flex gap-4 mb-3">
          <CFormSwitch id="gps" label="GPS" checked={gps} onChange={(e) => setGps(e.target.checked)} />
          <CFormSwitch id="lte" label="LTE" checked={lte} onChange={(e) => setLte(e.target.checked)} />
          <CFormSwitch id="wifi" label="WiFi" checked={wifi} onChange={(e) => setWifi(e.target.checked)} />
          <CFormSwitch id="lora" label="LoRa" checked={lora} onChange={(e) => setLora(e.target.checked)} />
        </div>

        <CFormLabel>Parametry</CFormLabel>
        <div className="small text-body-secondary mb-2">
          Cokoli, co je u téhle zakázky potřeba vědět — třeba „Svorkovnice → WAGO 221“ nebo „IO1 → PG9“.
        </div>
        <datalist id="parameter-keys">
          {lookups?.parameterKeys.map((k) => <option key={k} value={k} />)}
        </datalist>
        {parameters.map((p, index) => (
          <CRow key={index} className="g-2 mb-2 align-items-center">
            <CCol xs={5}>
              <CFormInput size="sm" list="parameter-keys" placeholder="Co" aria-label={`Parametr ${index + 1}`}
                value={p.key} onChange={(e) => setParameter(index, { key: e.target.value })} />
            </CCol>
            <CCol xs={6}>
              <CFormInput size="sm" placeholder="Hodnota" aria-label={`Hodnota ${index + 1}`}
                value={p.value} onChange={(e) => setParameter(index, { value: e.target.value })} />
            </CCol>
            <CCol xs={1}>
              <CButton size="sm" color="secondary" variant="ghost" aria-label="Odebrat parametr"
                onClick={() => setParameters(parameters.filter((_, i) => i !== index))}>×</CButton>
            </CCol>
          </CRow>
        ))}
        <div className="mb-3">
          <CButton size="sm" color="primary" variant="ghost"
            onClick={() => setParameters([...parameters, { key: '', value: '' }])}>+ parametr</CButton>
        </div>

        <CFormTextarea label="Poznámka ke konfiguraci" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        {error && <CAlert color="danger" className="mt-3 mb-0">{error}</CAlert>}
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" variant="ghost" onClick={onClose}>Zpět</CButton>
        <CButton color="primary" onClick={() => void save()}>Uložit</CButton>
      </CModalFooter>
    </CModal>
  );
}

function AddItem({ contract, onClose, onSaved }: { contract: Contract; onClose: () => void; onSaved: (c: Contract) => void }) {
  const lookups = useLookups();
  const [deviceType, setDeviceType] = useState<number | ''>('');
  const [customName, setCustomName] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (deviceType === '') return;

    const { data, error: problem } = await api.POST('/api/v1/contracts/{contractId}/items', {
      params: { path: { contractId: contract.id } },
      body: {
        expectedRowVersion: contract.rowVersion,
        item: {
          portalDeviceTypeId: deviceType, quantity, label: label || null, note: null,
          customName: deviceType === CUSTOM_DEVICE_TYPE ? customName.trim() : null,
        },
      },
    });

    if (data) onSaved(data); else setError(problemMessage(problem));
  }

  return (
    <CModal visible onClose={onClose}>
      <CModalHeader><CModalTitle>Přidat položku</CModalTitle></CModalHeader>
      <CModalBody>
        <CFormLabel>Zařízení</CFormLabel>
        <div className="mb-3">
          <DeviceTypeSelect deviceTypes={lookups?.deviceTypes} value={deviceType} customName={customName} placeholder="Vyberte…"
            onChange={(value, name) => { setDeviceType(value); setCustomName(name); }} />
        </div>
        <CRow className="g-3">
          <CCol xs={4}>
            <CFormLabel>Kusů</CFormLabel>
            <CFormInput type="number" min={1} value={quantity} onChange={(e) => setQuantity(Number(e.target.value))} />
          </CCol>
          <CCol xs={8}>
            <CFormLabel>Označení (nepovinné)</CFormLabel>
            <CFormInput value={label} onChange={(e) => setLabel(e.target.value)} placeholder="např. Záloha" />
          </CCol>
        </CRow>
        <div className="small text-body-secondary mt-3">Konfiguraci můžete vyplnit hned potom, nebo ji doplní vedení.</div>
        {error && <CAlert color="danger" className="mt-3 mb-0">{error}</CAlert>}
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" variant="ghost" onClick={onClose}>Zpět</CButton>
        <CButton color="primary" disabled={!deviceChosen(deviceType, customName)} onClick={() => void save()}>Přidat</CButton>
      </CModalFooter>
    </CModal>
  );
}
